// AlertModal component for displaying error, success, warning, or info messages
// Best practice: Replaces browser alerts with a consistent, accessible modal UI
//
// Usage:
//   <AlertModal 
//     isOpen={showAlert}
//     onClose={() => setShowAlert(false)}
//     title="Error"
//     message="Something went wrong"
//     type="error"
//   />

import React from 'react';
import { Dialog } from '@headlessui/react';
import { XCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';

const AlertModal = ({
    isOpen,
    onClose,
    title = 'Alert',
    message = '',
    type = 'info', // 'error', 'success', 'warning', 'info'
    buttonText = 'OK',
}) => {
    // Get icon and colors based on type (matching ConfirmationModal design)
    const getTypeConfig = () => {
        switch (type) {
            case 'error':
                return {
                    icon: XCircle,
                    iconColor: 'text-red-600',
                    buttonClass: 'bg-red-100 text-red-700 hover:bg-red-200',
                };
            case 'success':
                return {
                    icon: CheckCircle,
                    iconColor: 'text-green-600',
                    buttonClass: 'bg-green-100 text-green-700 hover:bg-green-200',
                };
            case 'warning':
                return {
                    icon: AlertTriangle,
                    iconColor: 'text-yellow-600',
                    buttonClass: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
                };
            default: // info
                return {
                    icon: Info,
                    iconColor: 'text-blue-600',
                    buttonClass: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
                };
        }
    };

    const { icon: Icon, iconColor, buttonClass } = getTypeConfig();

    // Ref for OK button
    const buttonRef = React.useRef(null);

    // Focus button when modal opens
    React.useEffect(() => {
        if (isOpen && buttonRef.current) {
            buttonRef.current.focus();
        }
    }, [isOpen]);

    // Handle Enter/Return key
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Return') {
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

            {/* Full-screen container */}
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="bg-white rounded-lg shadow-lg max-w-md w-full">
                    <div className="p-6">
                        <div className="flex items-start gap-4">
                            {/* Icon */}
                            <div className={`flex-shrink-0 ${iconColor}`}>
                                <Icon size={24} />
                            </div>

                            {/* Content */}
                            <div className="flex-1">
                                <Dialog.Title className="text-lg font-bold mb-2 text-gray-900">
                                    {title}
                                </Dialog.Title>
                                {message && (
                                    <Dialog.Description className="text-sm text-gray-600 whitespace-pre-line">
                                        {message}
                                    </Dialog.Description>
                                )}
                            </div>
                        </div>

                        {/* Button */}
                        <div className="mt-6 flex justify-end">
                            <button
                                ref={buttonRef}
                                onClick={onClose}
                                onKeyDown={handleKeyDown}
                                className={`px-6 py-2 rounded-md font-medium transition-colors ${buttonClass}`}
                            >
                                {buttonText}
                            </button>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export default AlertModal;
